import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { LockKeyhole, MapPin } from 'lucide-react'
import { Logo } from '@/components/generals/logo'
import { ButtonLink } from '@/components/generals/button'
import { mainNav } from '@/constants/site'

/*
   Footer
   Four columns on desktop (brand, explore, residents, location) that stack
   on mobile, with a hairline-separated legal row underneath.
*/
export const SiteFooter: FC = (): ReactNode => {
    return (
        <footer className='bg-forest-deep text-cream'>
            <div className='mx-auto max-w-7xl px-6 pb-10 pt-20 lg:px-10'>
                <div className='grid gap-14 lg:grid-cols-12 lg:gap-10'>
                    <div className='lg:col-span-5'>
                        <Logo tone='light' />

                        <p className='mt-7 max-w-sm text-sm leading-relaxed text-cream/70'>
                            Fourteen freestanding, single-storey homes behind one secure gate in central Hillcrest. Viewings are by private appointment.
                        </p>

                        <ButtonLink href='/#enquire' variant='outline-light' size='sm' className='mt-8'>
                            Arrange a private viewing
                        </ButtonLink>
                    </div>

                    <nav aria-label='Footer' className='lg:col-span-2'>
                        <h2 className='text-[0.6875rem] font-medium uppercase tracking-[0.3em] text-cream/50'>
                            Explore
                        </h2>

                        <ul className='mt-5 space-y-3 text-sm'>
                            {mainNav.map(link => (
                                <li key={link.href}>
                                    <Link href={link.href} className='link-underline text-cream/80 transition-colors hover:text-cream'>
                                        {link.label}
                                    </Link>
                                </li>
                            ))}

                            <li>
                                <Link href='/#enquire' className='link-underline text-cream/80 transition-colors hover:text-cream'>
                                    Book a viewing
                                </Link>
                            </li>
                        </ul>
                    </nav>

                    <div className='lg:col-span-2'>
                        <h2 className='text-[0.6875rem] font-medium uppercase tracking-[0.3em] text-cream/50'>
                            Residents
                        </h2>

                        <ul className='mt-5 space-y-3 text-sm'>
                            <li>
                                <Link href='/residents' className='link-underline inline-flex items-center gap-2 text-cream/80 transition-colors hover:text-cream'>
                                    <LockKeyhole aria-hidden='true' className='size-3.5' />
                                    Resident Access
                                </Link>
                            </li>

                            <li>
                                <Link href='/residents/access' className='link-underline text-cream/80 transition-colors hover:text-cream'>
                                    First-time access
                                </Link>
                            </li>
                        </ul>
                    </div>

                    <div className='lg:col-span-3'>
                        <h2 className='text-[0.6875rem] font-medium uppercase tracking-[0.3em] text-cream/50'>
                            Location
                        </h2>

                        <p className='mt-5 flex gap-3 text-sm leading-relaxed text-cream/80'>
                            <MapPin aria-hidden='true' className='mt-0.5 size-4 shrink-0 text-cream/50' />
                            <span>
                                Central Hillcrest and Hilldene
                                <br />
                                Hillcrest, KwaZulu-Natal
                                <br />
                                South Africa
                            </span>
                        </p>
                    </div>
                </div>

                <div className='mt-16 flex flex-col gap-3 border-t border-cream/10 pt-8 text-xs text-cream/55 sm:flex-row sm:items-center sm:justify-between'>
                    <p>
                        © <span suppressHydrationWarning>{new Date().getFullYear()}</span> Rockwood Estate. All rights reserved.
                    </p>

                    <p>
                        Images are for illustrative purposes only.
                    </p>
                </div>
            </div>
        </footer>
    )
}
